import { Device, Trip, User } from "@fairfleet/geotab";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  GridColDef,
  GridCsvExportMenuItem,
  GridRowSelectionModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExportContainer,
} from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { I18nGrid } from "@/components/data/I18nGrid";
import { DateCell } from "@/components/grid/cells/DateCell";
import GridExcelExportMenuItem from "@/components/grid/CustomToolbar/GridExcelExportMenuItem";
import { DistanceCell } from "@/components/logbook/DistanceCell";
import { NoRowsOverlay } from "@/components/logbook/NoRowsOverlay";
import { StartAddressCell } from "@/components/logbook/StartAddressCell";
import { StopAddressCell } from "@/components/logbook/StopAddressCell";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import { DriverEmailCell } from "@/pages/logbook-edit/components/grid/cells/DriverEmailCell";
import { DriverNameCell } from "@/pages/logbook-edit/components/grid/cells/DriverNameCell";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";
import { getUserEmail } from "@/utilities/user/getUserEmail";

/** Odometer readings arrive in metres; nobody reads a lorry's mileage in metres. */
function formatOdometer(metres: number | undefined): string {
  if (!Number.isFinite(metres)) {
    return "";
  }
  return `${Math.round((metres ?? 0) / 1000).toLocaleString()} km`;
}

interface ToolbarProps {
  monochrome: boolean;
  onMonochromeChange: (monochrome: boolean) => void;
}

// What this grid hands its own toolbar. The grid types slot props through this interface, so it
// has to be declared here rather than passed straight through.
declare module "@mui/x-data-grid-pro" {
  interface ToolbarPropsOverrides extends ToolbarProps {}
}

/**
 * The columns button, the exports, and the one setting the map takes from this page.
 *
 * The colour switch lives here rather than on the map because it is a property of the whole
 * picture, and the map's own corners are taken by the legends it produces.
 */
function RouteToolbar({ monochrome, onMonochromeChange }: ToolbarProps) {
  const { t } = useTranslation();

  return (
    <GridToolbarContainer sx={{ gap: 1, flexWrap: "wrap", justifyContent: "space-between" }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <GridToolbarColumnsButton />
        <GridToolbarExportContainer>
          <GridCsvExportMenuItem />
          <GridExcelExportMenuItem />
        </GridToolbarExportContainer>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1 }}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {t("route.gradient")}
        </Typography>
        <Slider
          size="small"
          min={0}
          max={1}
          step={1}
          marks
          value={monochrome ? 1 : 0}
          onChange={(_event, next) => onMonochromeChange(next === 1)}
          aria-label={t("route.colorMode")}
          sx={{ width: 120 }}
        />
        <Typography variant="caption" color="text.secondary" noWrap>
          {t("route.monochrome")}
        </Typography>
      </Stack>
    </GridToolbarContainer>
  );
}

interface Props {
  trips: Trip[] | undefined;
  selectedTripIds: GridRowSelectionModel;
  onSelectedTripIdsChange: (selection: GridRowSelectionModel) => void;
  /** `true` draws one colour per vehicle instead of a colour per speed. */
  monochrome: boolean;
  onMonochromeChange: (monochrome: boolean) => void;
}

/**
 * The journeys, as a list to pick from.
 *
 * The tick boxes are the point of this grid: what is ticked is what the map draws. Clicking the
 * row itself deliberately does not tick it — a reader reaching for the address in a row would
 * otherwise redraw the map by accident.
 */
export function RouteTripsGrid({
  trips,
  selectedTripIds,
  onSelectedTripIdsChange,
  monochrome,
  onMonochromeChange,
}: Props) {
  const { t } = useTranslation();

  const columns = useMemo<GridColDef<Trip>[]>(
    () => [
      {
        field: "device.name",
        headerName: t("device"),
        width: 220,
        valueGetter: (_value, row) => (row.device as Device).name ?? "",
        filterable: false,
        sortable: false,
      },
      {
        field: "device.licensePlate",
        headerName: t("faultOverview.licensePlate"),
        width: 140,
        valueGetter: (_value, row) => getDeviceLicensePlate(row.device as Device),
        filterable: false,
        sortable: false,
      },
      {
        field: "driver.name",
        headerName: t("driver"),
        width: 180,
        renderCell: props => <DriverNameCell props={props} />,
        valueGetter: (_value, row) => {
          const driver = row.driver as User;
          return driver?.firstName ? getUserDisplayName(driver) : "";
        },
        filterable: false,
        sortable: false,
      },
      {
        field: "driver.email",
        headerName: t("driverEmail"),
        width: 220,
        renderCell: props => <DriverEmailCell props={props} />,
        valueGetter: (_value, row) => getUserEmail(row.driver as User) ?? "",
        filterable: false,
        sortable: false,
      },
      {
        field: "odometerStart",
        headerName: t("route.odometerStart"),
        width: 170,
        // Geotab only reports the reading at the end of a journey; the one at the start is that
        // less the distance driven.
        valueGetter: (_value, row) => formatOdometer((row.odometer ?? 0) - (row.distance ?? 0) * 1000),
        filterable: false,
        sortable: false,
      },
      {
        field: "odometerEnd",
        headerName: t("route.odometerEnd"),
        width: 170,
        valueGetter: (_value, row) => formatOdometer(row.odometer),
        filterable: false,
        sortable: false,
      },
      {
        field: "distance",
        headerName: t("distance"),
        width: 130,
        renderCell: DistanceCell,
        filterable: false,
        sortable: false,
      },
      {
        field: "startAddress",
        headerName: t("startAddress"),
        width: 300,
        renderCell: StartAddressCell,
        filterable: false,
        sortable: false,
      },
      {
        field: "start",
        headerName: t("start"),
        width: 170,
        renderCell: DateCell,
        filterable: false,
        sortable: false,
      },
      {
        field: "stopAddress",
        headerName: t("stopAddress"),
        width: 300,
        renderCell: StopAddressCell,
        filterable: false,
        sortable: false,
      },
      {
        field: "stop",
        headerName: t("stop"),
        width: 170,
        renderCell: DateCell,
        filterable: false,
        sortable: false,
      },
    ],
    [t],
  );

  return (
    <I18nGrid
      sx={{ height: "100%", minHeight: 0 }}
      rows={trips ?? []}
      columns={columns}
      loading={!trips}
      getRowId={row => row.id}
      checkboxSelection
      disableColumnFilter
      disableDensitySelector
      disableRowSelectionOnClick
      rowSelectionModel={selectedTripIds}
      onRowSelectionModelChange={onSelectedTripIdsChange}
      pinnedColumns={{ left: ["__check__", "device.name"] }}
      slots={{ toolbar: RouteToolbar, noRowsOverlay: NoRowsOverlay }}
      slotProps={{ toolbar: { monochrome, onMonochromeChange } }}
    />
  );
}
